// Athlete-side equestrian show-record editor (Tier 3 SP4). Rendered by
// GameStatsPage for equestrian athletes in place of the position/year stat
// table. TWO always-visible add blocks — NCEA (win/loss by event) and IHSA
// (placement by ladder/level, with live-derived points) — plus a combined
// results list with edit/delete. Picklists are catalog-driven (pathways).
// Mirrors TrackMarksEditor, including the ConfirmModal when editing a verified
// row (the backend clears the blue dot on any content edit). The derived
// points/summary preview uses the same pure summarize() the API ledger uses.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCatalog } from '../../context/CatalogContext';
import Button from '../Button';
import ConfirmModal from '../ConfirmModal';
import VerifiedFlame from '../VerifiedFlame';
import {
  ihsaPointsFor, summarize,
  FORMAT_LABEL, NCEA_EVENT_LABEL, LADDER_LABEL, RESULT_LABEL, QUALIFIED_FOR_LABEL,
  QUALIFIED_FOR_OPTIONS,
} from '../../lib/equestrian';
import { resolveGetConfig } from './editorConfig';
import './AthleteEditor.css';

const currentYear = () => new Date().getFullYear();

const EMPTY_NCEA = {
  event: '', result: 'win', meet_name: '', meet_date: '', season_year: '',
  opponent: '',
};
const EMPTY_IHSA = {
  ladder: 'hunt_seat', level: '', placement: '', points: '', qualified_for: '',
  meet_name: '', meet_date: '', season_year: '', drawn_horse: '',
};

export default function EquestrianResultsEditor({ getConfig: getConfigProp }) {
  const { API_URL } = useAuth();
  const catalog = useCatalog();
  const [results, setResults] = useState([]);
  const [nceaForm, setNceaForm] = useState(EMPTY_NCEA);
  const [ihsaForm, setIhsaForm] = useState(EMPTY_IHSA);
  const [editing, setEditing] = useState(null); // { id, format }
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const pathways = catalog.pathwaysFor('equestrian') || {};
  const nceaEvents = pathways.ncea?.events || [];
  const ihsaLadders = pathways.ihsa?.ladders || {};
  const ladderKeys = Object.keys(ihsaLadders);
  const levelsForLadder = ihsaLadders[ihsaForm.ladder] || [];

  const getConfig = resolveGetConfig(getConfigProp);

  useEffect(() => { fetchResults(); }, []);

  const fetchResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/equestrian-results/me`, getConfig());
      // List endpoint returns { results, summary }.
      setResults(Array.isArray(res.data?.results) ? res.data.results : []);
    } catch (err) {
      console.error('Error fetching results:', err);
    }
  };

  const showMsg = (text, type = '') => {
    setMessage(text); setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const setNcea = (patch) => setNceaForm(prev => ({ ...prev, ...patch }));
  const setIhsa = (patch) => setIhsaForm(prev => ({ ...prev, ...patch }));

  const seasonFrom = (form) => (
    form.season_year ? Number(form.season_year)
      : (form.meet_date ? Number(form.meet_date.slice(0, 4)) : null)
  );

  const buildNceaPayload = () => {
    const yr = seasonFrom(nceaForm);
    return {
      format: 'ncea',
      event: nceaForm.event,
      result: nceaForm.result,
      opponent: nceaForm.opponent.trim() || null,
      meet_name: nceaForm.meet_name.trim() || null,
      meet_date: nceaForm.meet_date || null,
      season_year: Number.isFinite(yr) ? yr : null,
    };
  };

  const buildIhsaPayload = () => {
    const yr = seasonFrom(ihsaForm);
    return {
      format: 'ihsa',
      ladder: ihsaForm.ladder,
      level: ihsaForm.level || null,
      placement: ihsaForm.placement !== '' ? Number(ihsaForm.placement) : null,
      points: ihsaForm.points !== '' ? Number(ihsaForm.points) : null,
      qualified_for: ihsaForm.qualified_for || null,
      drawn_horse: ihsaForm.drawn_horse.trim() || null,
      meet_name: ihsaForm.meet_name.trim() || null,
      meet_date: ihsaForm.meet_date || null,
      season_year: Number.isFinite(yr) ? yr : null,
    };
  };

  // Live-derived points for the IHSA add form.
  const ihsaLivePoints = ihsaPointsFor(
    ihsaForm.placement !== '' ? Number(ihsaForm.placement) : null,
    ihsaForm.points !== '' ? Number(ihsaForm.points) : null,
  );

  const resetNcea = () => { setNceaForm(EMPTY_NCEA); if (editing?.format === 'ncea') setEditing(null); };
  const resetIhsa = () => { setIhsaForm(EMPTY_IHSA); if (editing?.format === 'ihsa') setEditing(null); };

  const doSave = async (format, payload) => {
    try {
      if (editing && editing.format === format) {
        await axios.patch(`${API_URL}/equestrian-results/me/${editing.id}`, payload, getConfig());
      } else {
        await axios.post(`${API_URL}/equestrian-results/me`, payload, getConfig());
      }
      if (format === 'ncea') resetNcea(); else resetIhsa();
      setEditing(null);
      fetchResults();
      showMsg('Result saved');
    } catch (err) {
      showMsg('Error saving result', 'error');
    }
  };

  const guardVerifiedThenSave = (format, payload) => {
    const editingVerified = editing && editing.format === format
      && results.find(r => r.id === editing.id)?.verified;
    if (editingVerified) {
      setConfirmAction({
        title: 'Unverify Result',
        message: 'This result is currently verified. Editing it will clear the verification, and it can only be re-verified by ProZpects staff or a Partner.',
        confirmLabel: 'Continue', danger: true,
        onConfirm: () => { setConfirmAction(null); doSave(format, payload); },
        onCancel: () => setConfirmAction(null),
      });
      return;
    }
    doSave(format, payload);
  };

  const saveNcea = () => {
    if (!nceaForm.event) { showMsg('Pick an event', 'error'); return; }
    guardVerifiedThenSave('ncea', buildNceaPayload());
  };

  const saveIhsa = () => {
    if (!ihsaForm.ladder) { showMsg('Pick a ladder', 'error'); return; }
    guardVerifiedThenSave('ihsa', buildIhsaPayload());
  };

  const startEdit = (r) => {
    if (r.format === 'ncea') {
      setEditing({ id: r.id, format: 'ncea' });
      setNceaForm({
        event: r.event || '',
        result: r.result || 'win',
        opponent: r.opponent || '',
        meet_name: r.meet_name || '',
        meet_date: r.meet_date || '',
        season_year: r.season_year || '',
      });
    } else if (r.format === 'ihsa') {
      setEditing({ id: r.id, format: 'ihsa' });
      setIhsaForm({
        ladder: r.ladder || 'hunt_seat',
        level: r.level || '',
        placement: r.placement != null ? String(r.placement) : '',
        points: r.points != null ? String(r.points) : '',
        qualified_for: r.qualified_for || '',
        drawn_horse: r.drawn_horse || '',
        meet_name: r.meet_name || '',
        meet_date: r.meet_date || '',
        season_year: r.season_year || '',
      });
    }
  };

  const deleteResult = async (id) => {
    try {
      await axios.delete(`${API_URL}/equestrian-results/me/${id}`, getConfig());
      if (editing?.id === id) setEditing(null);
      fetchResults();
      showMsg('Result removed');
    } catch (err) {
      console.error('Error deleting result:', err);
    }
  };

  // Live summary preview — same pure function the API ledger uses.
  const summary = useMemo(() => summarize(results), [results]);
  const nceaEventKeys = Object.keys(summary.ncea);
  const ihsaLadderKeys = Object.keys(summary.ihsa);

  // Combined results list, NCEA first.
  const grouped = useMemo(() => {
    const order = { ncea: 0, ihsa: 1 };
    const byFormat = new Map();
    for (const r of results) {
      if (!byFormat.has(r.format)) byFormat.set(r.format, []);
      byFormat.get(r.format).push(r);
    }
    return [...byFormat.entries()]
      .sort((a, b) => (order[a[0]] ?? 9) - (order[b[0]] ?? 9))
      .map(([fmt, rows]) => ({ fmt, label: FORMAT_LABEL[fmt] || fmt, rows }));
  }, [results]);

  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear() - i);
  const editingNcea = editing?.format === 'ncea';
  const editingIhsa = editing?.format === 'ihsa';

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      {/* ── NCEA block ─────────────────────────────────────────────── */}
      <div className="settings-section">
        <h2 className="settings-section__title">NCEA Results</h2>
        <p className="settings-section__desc">
          Head-to-head win/loss by event. Your record per event is tallied automatically.
        </p>

        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label className="form-field__label">Event</label>
            <select
              className="form-field__select"
              value={nceaForm.event}
              onChange={(e) => setNcea({ event: e.target.value })}
              data-testid="ncea_event"
            >
              <option value="">Select event…</option>
              {nceaEvents.map(ev => (
                <option key={ev.key} value={ev.key}>{ev.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 140 }}>
            <label className="form-field__label">Result</label>
            <select
              className="form-field__select"
              value={nceaForm.result}
              onChange={(e) => setNcea({ result: e.target.value })}
              data-testid="ncea_result"
            >
              <option value="win">Win</option>
              <option value="loss">Loss</option>
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label">Opponent</label>
            <input
              type="text"
              className="form-field__input"
              value={nceaForm.opponent}
              onChange={(e) => setNcea({ opponent: e.target.value })}
              placeholder="e.g., Auburn"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Meet</label>
            <input
              type="text"
              className="form-field__input"
              value={nceaForm.meet_name}
              onChange={(e) => setNcea({ meet_name: e.target.value })}
              placeholder="e.g., SEC Championship"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Date</label>
            <input
              type="date"
              className="form-field__input"
              value={nceaForm.meet_date}
              onChange={(e) => setNcea({ meet_date: e.target.value })}
            />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Season</label>
            <select
              className="form-field__select"
              value={nceaForm.season_year}
              onChange={(e) => setNcea({ season_year: e.target.value })}
            >
              <option value="">Year…</option>
              {yearOptions.map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={saveNcea} data-testid="ncea_save">
            {editingNcea ? 'Save Changes' : 'Add NCEA Result'}
          </Button>
          {editingNcea && (
            <Button type="button" variant="ghost" size="sm" onClick={resetNcea}>Cancel</Button>
          )}
        </div>

        {nceaEventKeys.length > 0 && (
          <div className="ae-eq-summary" style={{ marginTop: 14 }}>
            <div className="ae-mark-group__head">Record by Event</div>
            <div className="ae-eq-summary__grid">
              {nceaEventKeys.map(ev => (
                <div key={ev} className="ae-eq-summary__cell">
                  <span className="ae-eq-summary__label">{NCEA_EVENT_LABEL[ev] || ev}</span>
                  <span className="ae-eq-summary__value">{summary.ncea[ev].wins}–{summary.ncea[ev].losses}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── IHSA block ─────────────────────────────────────────────── */}
      <div className="settings-section">
        <h2 className="settings-section__title">IHSA Results</h2>
        <p className="settings-section__desc">
          Placement points by ladder toward a qualifying tier. Points are derived from your
          placement automatically (1st=7 … 6th=1); enter an override only if your show scores differently.
        </p>

        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 180 }}>
            <label className="form-field__label">Ladder</label>
            <select
              className="form-field__select"
              value={ihsaForm.ladder}
              onChange={(e) => setIhsa({ ladder: e.target.value, level: '' })}
              data-testid="ihsa_ladder"
            >
              {ladderKeys.map(k => (
                <option key={k} value={k}>{LADDER_LABEL[k] || k}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label">Level</label>
            <select
              className="form-field__select"
              value={ihsaForm.level}
              onChange={(e) => setIhsa({ level: e.target.value })}
              data-testid="ihsa_level"
            >
              <option value="">Select level…</option>
              {levelsForLadder.map(lvl => (
                <option key={lvl} value={lvl}>{lvl}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Placement</label>
            <select
              className="form-field__select"
              value={ihsaForm.placement}
              onChange={(e) => setIhsa({ placement: e.target.value })}
              data-testid="ihsa_placement"
            >
              <option value="">—</option>
              {[1, 2, 3, 4, 5, 6].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 130 }}>
            <label className="form-field__label">Points override</label>
            <input
              type="number"
              className="form-field__input"
              value={ihsaForm.points}
              onChange={(e) => setIhsa({ points: e.target.value })}
              placeholder="auto"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 160 }}>
            <label className="form-field__label">Qualified For</label>
            <select
              className="form-field__select"
              value={ihsaForm.qualified_for}
              onChange={(e) => setIhsa({ qualified_for: e.target.value })}
            >
              {QUALIFIED_FOR_OPTIONS.map(o => (
                <option key={o.value || 'none'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 130, justifyContent: 'flex-end' }}>
            <label className="form-field__label">Points</label>
            <div className="ae-eq-derived" data-testid="ihsa_derived_points">
              {ihsaLivePoints != null ? `${ihsaLivePoints} pts` : '—'}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Show / Meet</label>
            <input
              type="text"
              className="form-field__input"
              value={ihsaForm.meet_name}
              onChange={(e) => setIhsa({ meet_name: e.target.value })}
              placeholder="e.g., Zone 6 Regionals"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Drawn Horse</label>
            <input
              type="text"
              className="form-field__input"
              value={ihsaForm.drawn_horse}
              onChange={(e) => setIhsa({ drawn_horse: e.target.value })}
              placeholder="e.g., Biscuit"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Date</label>
            <input
              type="date"
              className="form-field__input"
              value={ihsaForm.meet_date}
              onChange={(e) => setIhsa({ meet_date: e.target.value })}
            />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Season</label>
            <select
              className="form-field__select"
              value={ihsaForm.season_year}
              onChange={(e) => setIhsa({ season_year: e.target.value })}
            >
              <option value="">Year…</option>
              {yearOptions.map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={saveIhsa} data-testid="ihsa_save">
            {editingIhsa ? 'Save Changes' : 'Add IHSA Result'}
          </Button>
          {editingIhsa && (
            <Button type="button" variant="ghost" size="sm" onClick={resetIhsa}>Cancel</Button>
          )}
        </div>

        {ihsaLadderKeys.length > 0 && (
          <div className="ae-eq-summary" style={{ marginTop: 14 }}>
            <div className="ae-mark-group__head">Points by Ladder / Season</div>
            <div className="ae-eq-summary__grid">
              {ihsaLadderKeys.map(ladder => (
                Object.keys(summary.ihsa[ladder]).sort().map(year => {
                  const b = summary.ihsa[ladder][year];
                  return (
                    <div key={`${ladder}-${year}`} className="ae-eq-summary__cell">
                      <span className="ae-eq-summary__label">{LADDER_LABEL[ladder] || ladder} · {year}</span>
                      <span className="ae-eq-summary__value">
                        {b.points} pts
                        {b.qualified_for && (
                          <span className="ae-mark-badge ae-mark-badge--sb">
                            {QUALIFIED_FOR_LABEL[b.qualified_for] || b.qualified_for}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Combined results list ──────────────────────────────────── */}
      <div className="settings-section">
        <h2 className="settings-section__title">Logged Results</h2>
        {results.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '13px' }}>No results logged yet.</p>
        )}
        {grouped.map(g => (
          <div key={g.fmt} className="ae-mark-group" style={{ marginTop: 14 }}>
            <div className="ae-mark-group__head">{g.label}</div>
            {g.rows.map(r => (
              <div key={r.id} className="accolade-row">
                <div className="accolade-row__body">
                  <span className="accolade-row__title">
                    {r.meet_name || (r.format === 'ncea' ? 'Dual Meet' : 'Show')}
                    {r.format === 'ncea' && r.result && (
                      <span className={`ae-mark-badge ${r.result === 'win' ? 'ae-mark-badge--win' : 'ae-mark-badge--loss'}`}>
                        {RESULT_LABEL[r.result] || r.result}
                      </span>
                    )}
                    {r.verified && <VerifiedFlame />}
                  </span>
                  <span className="accolade-row__meta">
                    {r.format === 'ncea' && r.event && (
                      <span className="accolade-row__src">{NCEA_EVENT_LABEL[r.event] || r.event}</span>
                    )}
                    {r.format === 'ncea' && r.opponent && <span className="accolade-row__src">vs {r.opponent}</span>}
                    {r.format === 'ihsa' && r.ladder && (
                      <span className="accolade-chip">{LADDER_LABEL[r.ladder] || r.ladder}</span>
                    )}
                    {r.format === 'ihsa' && r.level && <span className="accolade-row__src">{r.level}</span>}
                    {r.format === 'ihsa' && r.placement != null && <span className="accolade-row__src">Place {r.placement}</span>}
                    {r.format === 'ihsa' && r.drawn_horse && <span className="accolade-row__src">Horse: {r.drawn_horse}</span>}
                    {r.meet_date && <span className="accolade-row__src">{r.meet_date}</span>}
                    {r.season_year && <span className="accolade-row__src">{r.season_year}</span>}
                  </span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
                <Button type="button" variant="danger" size="sm" onClick={() => deleteResult(r.id)}>Delete</Button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </>
  );
}
